import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AgentTask,
  AgentTaskStatus,
  AgentTaskThreadMessage,
  AgentThreadRole,
  AgentToolCallAudit,
} from '../types';
import {
  addAgentTaskThreadMessage,
  createAgentTask,
  deleteAgentTask,
  ensureAgentTaskHasSystemThreadSeed,
  subscribeAgentTaskToolCalls,
  subscribeAgentTaskThread,
  subscribeAgentTasks,
  updateAgentTaskDetails,
  updateAgentTaskStatus,
} from '../services/agentTasks';

interface UseAgentTasksResult {
  tasks: AgentTask[];
  loading: boolean;
  createTask: (input: { title: string; description: string }) => Promise<string | null>;
  updateTaskDetails: (taskId: string, input: { title: string; description: string }) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
  updateStatus: (taskId: string, status: AgentTaskStatus) => Promise<void>;
  thread: AgentTaskThreadMessage[];
  threadLoading: boolean;
  toolCalls: AgentToolCallAudit[];
  toolCallsLoading: boolean;
  selectedTaskId: string | null;
  setSelectedTaskId: (taskId: string | null) => void;
  sendThreadMessage: (content: string, role?: AgentThreadRole) => Promise<void>;
  groupedTasks: {
    pending: AgentTask[];
    active: AgentTask[];
    completed: AgentTask[];
    failed: AgentTask[];
  };
}

export function useAgentTasks(userId: string | null): UseAgentTasksResult {
  const [tasks, setTasks] = useState<AgentTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [thread, setThread] = useState<AgentTaskThreadMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [toolCalls, setToolCalls] = useState<AgentToolCallAudit[]>([]);
  const [toolCallsLoading, setToolCallsLoading] = useState(false);

  useEffect(() => {
    if (!userId) {
      setTasks([]);
      setLoading(false);
      setSelectedTaskId(null);
      return;
    }

    setLoading(true);
    const unsub = subscribeAgentTasks(
      userId,
      (nextTasks) => {
        setTasks(nextTasks);
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsub;
  }, [userId]);

  useEffect(() => {
    if (!selectedTaskId) {
      setThread([]);
      setThreadLoading(false);
      setToolCalls([]);
      setToolCallsLoading(false);
      return;
    }
    if (!userId) return;

    setThreadLoading(true);
    setToolCallsLoading(true);
    const unsubThread = subscribeAgentTaskThread(
      userId,
      selectedTaskId,
      (messages) => {
        setThread(messages);
        setThreadLoading(false);
      },
      () => setThreadLoading(false)
    );
    const unsubToolCalls = subscribeAgentTaskToolCalls(
      userId,
      selectedTaskId,
      (audits) => {
        setToolCalls(audits);
        setToolCallsLoading(false);
      },
      () => setToolCallsLoading(false)
    );
    return () => {
      unsubThread();
      unsubToolCalls();
    };
  }, [selectedTaskId, userId]);

  useEffect(() => {
    if (!tasks.length) {
      setSelectedTaskId(null);
      return;
    }
    if (selectedTaskId && tasks.some(task => task.id === selectedTaskId)) return;
    setSelectedTaskId(tasks[0].id);
  }, [selectedTaskId, tasks]);

  const createTask = useCallback(async (
    input: { title: string; description: string }
  ): Promise<string | null> => {
    if (!userId) return null;
    const title = input.title.trim();
    if (!title) return null;
    const taskId = await createAgentTask(userId, {
      title,
      description: input.description,
      status: 'pending',
      source: 'manual',
    });
    await ensureAgentTaskHasSystemThreadSeed(userId, taskId);
    setSelectedTaskId(taskId);
    return taskId;
  }, [userId]);

  const updateStatus = useCallback(async (taskId: string, status: AgentTaskStatus): Promise<void> => {
    if (!userId) return;
    await updateAgentTaskStatus(userId, taskId, status);
  }, [userId]);

  const updateDetails = useCallback(async (
    taskId: string,
    input: { title: string; description: string }
  ): Promise<void> => {
    if (!userId) return;
    await updateAgentTaskDetails(userId, taskId, input);
  }, [userId]);

  const deleteTaskById = useCallback(async (taskId: string): Promise<void> => {
    if (!userId) return;
    await deleteAgentTask(userId, taskId);
    if (selectedTaskId === taskId) {
      setSelectedTaskId(null);
    }
  }, [selectedTaskId, userId]);

  const sendThreadMessage = useCallback(async (content: string, role: AgentThreadRole = 'user'): Promise<void> => {
    if (!userId || !selectedTaskId) return;
    await addAgentTaskThreadMessage(userId, selectedTaskId, { role, content });
  }, [selectedTaskId, userId]);

  const groupedTasks = useMemo(() => ({
    pending: tasks.filter(task => task.status === 'pending'),
    active: tasks.filter(task => task.status === 'active'),
    completed: tasks.filter(task => task.status === 'completed'),
    failed: tasks.filter(task => task.status === 'failed'),
  }), [tasks]);

  return {
    tasks,
    loading,
    createTask,
    updateTaskDetails: updateDetails,
    deleteTask: deleteTaskById,
    updateStatus,
    thread,
    threadLoading,
    toolCalls,
    toolCallsLoading,
    selectedTaskId,
    setSelectedTaskId,
    sendThreadMessage,
    groupedTasks,
  };
}
